import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { i18n } from '@kostbar/i18n'
import type {
  AdminUser,
  CreateUserRequest,
  UpdateUserRequest,
  UpdateUserRolesRequest,
  UsersListResponse,
} from '@/api/types'
import { usersApi } from '@/api/endpoints/users'
import { ApiError } from '@/api/types'
import { getErrorMessage } from '@/shared/utils/error-codes'

interface UseUsersParams {
  search?: string
  role?: string
  status?: 'active' | 'disabled'
  page?: number
  pageSize?: number
  tenantId?: number | null
  enabled?: boolean
}

export function useUsers(params?: UseUsersParams) {
  return useQuery<UsersListResponse>({
    queryKey: ['users', params],
    queryFn: () => usersApi.list(params),
    enabled: params?.enabled ?? true,
  })
}

export function useUser(id?: number | null, tenantId?: number | null) {
  return useQuery<AdminUser>({
    queryKey: ['users', id, tenantId],
    queryFn: () => {
      if (!id) throw new Error('user_id_required')
      return usersApi.get(id, tenantId)
    },
    enabled: Boolean(id && tenantId),
  })
}

export function useCreateUser(tenantId?: number | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateUserRequest) => usersApi.create(data, tenantId),
    onSuccess: () => {
      toast.success(i18n.t('users:toast.createSuccess'))
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        toast.error(getErrorMessage(error.status, error.code))
      } else {
        toast.error(i18n.t('users:toast.createFailed'))
      }
    },
  })
}

export function useUpdateUser(tenantId?: number | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateUserRequest }) =>
      usersApi.update(id, data, tenantId),
    onSuccess: (result) => {
      toast.success(i18n.t('users:toast.updateSuccess'))
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['users', result.user.id] })
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        toast.error(getErrorMessage(error.status, error.code))
      } else {
        toast.error(i18n.t('users:toast.updateFailed'))
      }
    },
  })
}

export function useUpdateUserRoles(tenantId?: number | null) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateUserRolesRequest }) =>
      usersApi.updateRoles(id, data, tenantId),
    onSuccess: (result) => {
      toast.success(i18n.t('users:toast.rolesUpdated'))
      queryClient.invalidateQueries({ queryKey: ['users'] })
      queryClient.invalidateQueries({ queryKey: ['users', result.user.id] })
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        toast.error(getErrorMessage(error.status, error.code))
      } else {
        toast.error(i18n.t('users:toast.rolesUpdateFailed'))
      }
    },
  })
}
